<script setup>
import { computed, onMounted, ref } from "vue";
import Modal from "bootstrap/js/dist/modal";
import { useContactStore } from "@/stores/contactStore";
import ContactsModal from "@/components/modal/contacts-modal.vue";

const title = "Contacts";
const text = "CRM";
const text1 = "Contacts Grid";

const store = useContactStore();
const editItem = ref(null);
const search = ref("");
const status = ref("");
const sort = ref("recent");

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function openModal(item = null) {
  editItem.value = item;
  const el = document.getElementById("add_contact");
  Modal.getOrCreateInstance(el)?.show();
}

async function removeContact(item) {
  if (!item?.id) return;
  if (!window.confirm(`Delete contact "${item.name}"?`)) return;
  await store.remove(item.id);
}

function exportCsv() {
  const rows = filteredContacts.value;
  const header = ["Name", "Account", "Email", "Phone", "Job Title", "Owner", "Status", "Created"];
  const body = rows.map((contact) => [
    contact.name || "",
    contact.client?.name || contact.company_name || "",
    contact.email || "",
    contact.phone || "",
    contact.job_title || "",
    ownerLabel(contact),
    contact.status || "",
    contact.created_at || "",
  ]);
  const csv = [header, ...body]
    .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `crm-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const filteredContacts = computed(() => {
  const q = search.value.trim().toLowerCase();
  let rows = Array.isArray(store.items) ? [...store.items] : [];

  if (status.value) {
    rows = rows.filter((contact) => String(contact.status || "").toLowerCase() === status.value.toLowerCase());
  }

  if (q) {
    rows = rows.filter((contact) => {
      const hay = [
        contact.name,
        contact.email,
        contact.phone,
        contact.job_title,
        contact.client?.name,
        contact.company_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }

  if (sort.value === "name_asc") rows.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  if (sort.value === "name_desc") rows.sort((a, b) => String(b.name || "").localeCompare(String(a.name || "")));
  if (sort.value === "recent") rows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  return rows;
});

function ownerLabel(contact) {
  const owner = contact?.owner;
  if (!owner) return "Unassigned";
  return [owner.first_name, owner.last_name].filter(Boolean).join(" ") || owner.email || "Unassigned";
}

function initials(name) {
  return String(name || "CT")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString();
}

function accountLabel(contact) {
  return contact?.client?.name || contact?.company_name || "No linked account";
}

onMounted(async () => {
  await store.fetchAll({ page: 1, per_page: 100, with_deals: 1 });
});
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <div class="me-2 mb-2">
            <div class="d-flex align-items-center border bg-white rounded p-1 me-2">
              <router-link to="/crm/contacts" class="btn btn-icon btn-sm me-1">
                <i class="ti ti-list-tree"></i>
              </router-link>
              <router-link to="/crm/contacts-grid" class="btn btn-icon btn-sm active bg-primary text-white">
                <i class="ti ti-layout-grid"></i>
              </router-link>
            </div>
          </div>

          <div class="me-2 mb-2">
            <button class="btn btn-white d-inline-flex align-items-center" @click="exportCsv">
              <i class="ti ti-file-type-csv me-1"></i>Export CSV
            </button>
          </div>

          <div class="mb-2">
            <button class="btn btn-primary d-flex align-items-center" @click="openModal()" v-can="'Contact.Create'">
              <i class="ti ti-circle-plus me-2"></i>Add Contact
            </button>
          </div>

          <div class="head-icons ms-2">
            <a href="javascript:void(0);" id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body p-3">
          <div class="row g-3 align-items-end">
            <div class="col-lg-5">
              <label class="form-label">Search</label>
              <input v-model.trim="search" class="form-control" placeholder="Search name, account, email, or phone" />
            </div>
            <div class="col-lg-3">
              <label class="form-label">Status</label>
              <select v-model="status" class="form-select">
                <option value="">All statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div class="col-lg-3">
              <label class="form-label">Sort</label>
              <select v-model="sort" class="form-select">
                <option value="recent">Recently Added</option>
                <option value="name_asc">Name A to Z</option>
                <option value="name_desc">Name Z to A</option>
              </select>
            </div>
            <div class="col-lg-1">
              <button class="btn btn-outline-secondary w-100" @click="store.fetchAll({ page: 1, per_page: 100, with_deals: 1 })">
                <i class="ti ti-refresh"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div v-if="store.loading && !filteredContacts.length" class="col-12">
          <div class="card">
            <div class="card-body py-5 text-center text-muted">Loading contacts...</div>
          </div>
        </div>

        <div v-else-if="!filteredContacts.length" class="col-12">
          <div class="card">
            <div class="card-body py-5 text-center text-muted">No contacts match the current filters.</div>
          </div>
        </div>

        <div v-for="contact in filteredContacts" :key="contact.id" class="col-xl-3 col-lg-4 col-md-6 d-flex">
          <div class="card w-100">
            <div class="card-body d-flex flex-column">
              <div class="d-flex justify-content-between align-items-start mb-3">
                <router-link
                  :to="{ name: 'crm.contacts.details', params: { id: contact.id } }"
                  class="avatar avatar-xl avatar-rounded border border-primary text-primary bg-light d-inline-flex align-items-center justify-content-center"
                >
                  <span class="fw-semibold">{{ initials(contact.name) }}</span>
                </router-link>

                <div class="dropdown">
                  <button class="btn btn-icon btn-sm rounded-circle" type="button" data-bs-toggle="dropdown">
                    <i class="ti ti-dots-vertical"></i>
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end p-2">
                    <li>
                      <button class="dropdown-item rounded-1" @click="openModal(contact)" v-can="'Contact.Edit'">
                        <i class="ti ti-edit me-1"></i>Edit
                      </button>
                    </li>
                    <li>
                      <button class="dropdown-item rounded-1 text-danger" @click="removeContact(contact)" v-can="'Contact.Delete'">
                        <i class="ti ti-trash me-1"></i>Delete
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="text-center mb-3">
                <h6 class="mb-1">
                  <router-link :to="{ name: 'crm.contacts.details', params: { id: contact.id } }">
                    {{ contact.name }}
                  </router-link>
                </h6>
                <span class="badge bg-pink-transparent fs-10 fw-medium">
                  {{ contact.job_title || "No title" }}
                </span>
              </div>

              <div class="d-flex flex-column gap-2 mb-3">
                <div class="text-dark d-inline-flex align-items-center">
                  <i class="ti ti-building text-gray-5 me-2"></i>
                  {{ accountLabel(contact) }}
                </div>
                <div class="text-dark d-inline-flex align-items-center">
                  <i class="ti ti-mail-forward text-gray-5 me-2"></i>
                  {{ contact.email || "No email" }}
                </div>
                <div class="text-dark d-inline-flex align-items-center">
                  <i class="ti ti-phone text-gray-5 me-2"></i>
                  {{ contact.phone || "No phone" }}
                </div>
                <div class="text-dark d-inline-flex align-items-center">
                  <i class="ti ti-user-star text-gray-5 me-2"></i>
                  {{ ownerLabel(contact) }}
                </div>
              </div>

              <div class="mt-auto d-flex align-items-center justify-content-between border-top pt-3">
                <span class="badge" :class="contact.status === 'Active' ? 'bg-success-transparent' : 'bg-danger-transparent'">
                  {{ contact.status || "Unknown" }}
                </span>
                <span class="text-muted fs-12">{{ formatDate(contact.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ContactsModal :edit-item="editItem" @saved="store.fetchAll({ page: 1, per_page: 100, with_deals: 1 })" />
</template>
